import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TipTapEditor } from '../../components/admin/TipTapEditor';
import { TipTapTitleEditor } from '../../components/admin/TipTapTitleEditor';
import { StructuredContentEditor } from '../../components/admin/StructuredContentEditor';
import { SlugField } from '../../components/admin/SlugField';
import { SEOFields } from '../../components/admin/SEOFields';
import { AutocompleteSelector } from '../../components/admin/AutocompleteSelector';
import { BlogTableOfContents } from '../../components/blog-v1/BlogTableOfContents';
import { AIArticleAssistant } from '../../components/admin/AIArticleAssistant';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { API_BASE } from '../../config/api';
import { getTitlePlainText, formatTitleForSubmission, getTitleJson } from '../../utils/titleFormat';

export const BlogForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [availableArticles, setAvailableArticles] = useState([]);
  
  // Fetch article categories from taxonomy
  const { data: taxonomyData, loading: taxonomyLoading } = useTaxonomy('article_category');
  const categories = taxonomyData.map(item => item.label);

  const [formData, setFormData] = useState({
    title: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }, // TipTap JSON format
    slug: '',
    category: '',
    tags: [],
    excerpt: '',
    content: { blocks: [] },
    content_sections: [],
    featured_image: '',
    article_intent: '',
    seo_title: '',
    seo_description: '',
    meta_title: '',
    meta_description: '',
    featured_article: false,
    featured_programs: [],
    ad_banners: [{image_url: '', target_url: ''}],
    published: false
  });

  const [originalSlug, setOriginalSlug] = useState('');

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchPrograms();
    fetchAvailableArticles();
    if (isEdit) {
      fetchBlog();
    }
  }, [id]);

  const fetchPrograms = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/programs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPrograms(data.programs || []);
      }
    } catch (err) {
      console.error('Failed to fetch programs:', err);
    }
  };

  const fetchAvailableArticles = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/blog_posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableArticles(data.blog_posts || []);
      }
    } catch (err) {
      console.error('Failed to fetch articles:', err);
    }
  };

  const fetchBlog = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/blog_posts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch blog');

      const data = await response.json();
      const blogData = data.blog_post;
      setOriginalSlug(blogData.slug || '');
      
      // Handle title format (legacy string or new rich-text format)
      const titleValue = typeof blogData.title === 'string' 
        ? getTitleJson(blogData.title)
        : (blogData.title?.json || { type: 'doc', content: [] });
      
      setFormData({
        ...blogData,
        title: titleValue, // Store as JSON for editor
        tags: blogData.tags || [],
        seo_title: blogData.seo_title || '',
        seo_description: blogData.seo_description || '',
        content: typeof blogData.content === 'string' 
          ? { blocks: [] } 
          : (blogData.content || { blocks: [] }),
        content_sections: blogData.content_sections || [],
        article_intent: blogData.article_intent || '',
        featured_programs: blogData.featured_programs || [],
        ad_banners: blogData.ad_banners && blogData.ad_banners.length > 0
          ? blogData.ad_banners
          : [{image_url: '', target_url: ''}]
      });
    } catch (err) {
      alert('Failed to load blog post');
      navigate('/admin/blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const url = isEdit
        ? `${backendUrl}/api/admin/blog_posts/${id}`
        : `${backendUrl}/api/admin/blog_posts`;

      // Format title for submission (convert TipTap JSON to structured format)
      const titleFormatted = typeof formData.title === 'string'
        ? formData.title // Legacy format - keep as string
        : formatTitleForSubmission(formData.title); // New format - structured object

      const payload = {
        ...formData,
        title: titleFormatted,
        ad_banners: formData.ad_banners.filter(
          banner => (banner.image_url && typeof banner.image_url === 'string' && banner.image_url.trim() !== '') || (banner.target_url && typeof banner.target_url === 'string' && banner.target_url.trim() !== '')
        )
      };

      console.log('BlogForm: Sending request to:', url);
      console.log('BlogForm: Payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('BlogForm: Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('BlogForm: Save failed with error:', errorData);
        throw new Error('Failed to save');
      }

      const result = await response.json();
      console.log('BlogForm: Save successful, result:', result);

      navigate('/admin/blogs');
    } catch (err) {
      console.error('BlogForm: Save error:', err);
      alert('Failed to save blog post');
    } finally {
      setSaving(false);
    }
  };

  const handleMultiSelectChange = (field, value) => {
    // Allow commas to be typed - split on blur or when user presses Enter
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTagsBlur = (e) => {
    const value = e.target.value;
    const values = value.split(',').map(v => v.trim()).filter(v => v !== '');
    setFormData(prev => ({ ...prev, tags: values }));
  };

  const addAdBanner = () => {
    setFormData(prev => ({
      ...prev,
      ad_banners: [...prev.ad_banners, {image_url: '', target_url: ''}]
    }));
  };

  const removeAdBanner = (index) => {
    setFormData(prev => ({
      ...prev,
      ad_banners: prev.ad_banners.filter((_, i) => i !== index)
    }));
  };

  const updateAdBanner = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ad_banners: prev.ad_banners.map((banner, i) =>
        i === index ? { ...banner, [field]: value } : banner
      )
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading blog post...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/admin/blogs')}
            className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2 mb-3"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog Posts
          </button>
          <h1 className="text-3xl font-bold text-[#111827]">{isEdit ? 'Edit' : 'Add'} Blog Post</h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-bold text-[#111827] mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Title * (Rich Text)</label>
                <TipTapTitleEditor
                  content={typeof formData.title === 'string' ? getTitleJson(formData.title) : formData.title}
                  onChange={(json) => {
                    setFormData({...formData, title: json});
                  }}
                  placeholder="Article title (supports bold, italic, underline, font size)"
                />
                <p className="text-xs text-[#6B7280] mt-1">Supports formatting: Bold, Italic, Underline, Font Size</p>
              </div>

              <div className="md:col-span-2">
                <SlugField
                  value={formData.slug}
                  onChange={(slug) => setFormData({...formData, slug})}
                  sourceValue={getTitlePlainText(formData.title)}
                  isEdit={isEdit}
                  originalSlug={originalSlug}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Category *</label>
                {taxonomyLoading ? (
                  <div className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-gray-50 text-gray-500">
                    Loading categories...
                  </div>
                ) : (
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">Tags</label>
                <input
                  type="text"
                  value={Array.isArray(formData.tags) ? formData.tags.join(', ') : formData.tags}
                  onChange={(e) => handleMultiSelectChange('tags', e.target.value)}
                  onBlur={handleTagsBlur}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="study-abroad, tips, scholarships (comma-separated)"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Featured Image URL</label>
                <input
                  type="url"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="https://..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Excerpt / Summary *</label>
                <textarea
                  required
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Short summary for previews and meta description"
                />
              </div>
            </div>
          </div>

          {/* AI Article Assistant */}
          <AIArticleAssistant
            onGenerate={(aiResponse) => {
              // Update form data with AI-generated content
              if (aiResponse.title) {
                // Handle title format - AI returns {plain_text, tiptap}
                const titleJson = aiResponse.title.tiptap || { type: 'doc', content: [] };
                setFormData(prev => ({
                  ...prev,
                  title: titleJson,
                  content_sections: aiResponse.content_sections || []
                }));
              } else if (aiResponse.content_sections) {
                setFormData(prev => ({
                  ...prev,
                  content_sections: aiResponse.content_sections
                }));
              }
            }}
            existingContentSections={formData.content_sections || []}
            availableArticles={availableArticles}
          />

          {/* Article Content - Structured Sections */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Article Content</h2>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-9">
                <StructuredContentEditor
                  contentSections={formData.content_sections || []}
                  onChange={(sections) => {
                    setFormData({...formData, content_sections: sections});
                  }}
                />
              </div>
              <div className="lg:col-span-3">
                <div className="sticky top-4">
                  <h3 className="text-sm font-semibold text-[#111827] mb-3">Table of Contents Preview</h3>
                  {formData.content_sections && formData.content_sections.length > 0 ? (
                    <BlogTableOfContents sections={formData.content_sections} />
                  ) : (
                    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4">
                      <p className="text-sm text-[#6B7280]">Add sections with headings to generate table of contents.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-[#6B7280] mt-2">Structured content with sections and blocks. Legacy content editor available if needed.</p>
          </div>

          {/* SEO Fields */}
          <SEOFields
            seoData={{ seo_title: formData.seo_title, seo_description: formData.seo_description }}
            onChange={(seoData) => setFormData({...formData, ...seoData})}
          />

          {/* Featured Programs */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Article Linking</h2>
            <AutocompleteSelector
              label="Featured Programs (Recommendations)"
              placeholder="Search programs..."
              selectedIds={formData.featured_programs}
              onChange={(ids) => setFormData({...formData, featured_programs: ids})}
              fetchItems={async () => {
                const token = localStorage.getItem('admin_token');
                const response = await fetch(`${backendUrl}/api/admin/programs`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                return data.programs || [];
              }}
              itemLabelKey="program_name"
              itemIdKey="id"
              helperText="These programs appear below the article as recommendations"
            />
          </div>

          {/* Ad Banners */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Ad Banners (Right Side)</h2>
            <div className="space-y-4">
              {formData.ad_banners.map((banner, index) => (
                <div key={index} className="border-2 border-[#E5E7EB] rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-[#111827]">Banner {index + 1}</h3>
                    {formData.ad_banners.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAdBanner(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-semibold"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <input
                      type="url"
                      value={banner.image_url}
                      onChange={(e) => updateAdBanner(index, 'image_url', e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      placeholder="Banner Image URL"
                    />
                    <input
                      type="url"
                      value={banner.target_url}
                      onChange={(e) => updateAdBanner(index, 'target_url', e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                      placeholder="Target URL (where banner links to)"
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addAdBanner}
                className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Banner
              </button>
            </div>
          </div>

          {/* Publish Options */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Publish Options</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured_article}
                  onChange={(e) => setFormData({...formData, featured_article: e.target.checked})}
                  className="w-5 h-5"
                />
                <label htmlFor="featured" className="text-sm font-semibold text-[#111827] cursor-pointer">
                  ⭐ Featured Article (appears at top of blog list)
                </label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({...formData, published: e.target.checked})}
                  className="w-5 h-5"
                />
                <label htmlFor="published" className="text-sm font-semibold text-[#111827] cursor-pointer">
                  Publish (visible on website)
                </label>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-6">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50"
            >
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')} Blog Post
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/blogs')}
              className="px-8 py-3 bg-white border-2 border-[#E5E7EB] text-[#111827] hover:border-[#1E40AF] rounded-lg font-semibold transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default BlogForm;